import hl7FieldKeyMapRaw from '../../../assets/rule-config/field-key-map-hl7.json';
import { normalizeXmlFieldPersistPath } from '../xml/xml-field-key-map.flatten';

/** Metadata for HL7 locate dialog: OBR component keys, OBX component keys, tail after `/OBX/`. */
export interface Hl7ObrObxLocateMeta {
  obrFieldKeys: string[];
  obxFieldKeys: string[];
  /** Path after `/OBX/`, e.g. `OBX.5/OBX.5.1`. */
  leafTail: string;
}

/** Flattened HL7 field entry (always object so routing meta helpers see `path`). */
export interface Hl7FieldMapMetaValue {
  path: string;
  isRepeatable?: boolean | string;
  repeatableNodes?: string[];
  hl7ObrObxLocate?: Hl7ObrObxLocateMeta;
}

export type Hl7FlattenedFieldMapValue = string | Hl7FieldMapMetaValue;

interface Hl7Node {
  name: string;
  label?: string;
  type?: string;
  xpath: string;
  isRepeatable?: boolean;
  attributes?: Hl7Node[];
  children?: Hl7Node[];
}

function isHl7TreeRoot(doc: unknown): doc is Hl7Node {
  if (!doc || typeof doc !== 'object') {
    return false;
  }
  const d = doc as Hl7Node;
  return d.name === 'HL7Message' && typeof d.xpath === 'string';
}

function segmentBareElement(seg: string): string {
  if (!seg || seg.startsWith('@')) {
    return '';
  }
  return seg.split('[')[0].split('[@')[0].trim();
}

function parentSegmentBeforeElement(fullPath: string, elementLocalName: string): string {
  const parts = normalizeXmlFieldPersistPath(fullPath).split('/').filter(Boolean);
  const el = (elementLocalName || '').trim();
  if (!el) {
    return '';
  }
  const idx = parts.findIndex((p) => segmentBareElement(p) === el);
  if (idx <= 0) {
    return '';
  }
  return segmentBareElement(parts[idx - 1]);
}

function pathsEqualHl7(a: string, b: string): boolean {
  return normalizeXmlFieldPersistPath(a) === normalizeXmlFieldPersistPath(b);
}

function existingPathInMap(
  map: Record<string, Hl7FlattenedFieldMapValue>,
  key: string
): string | undefined {
  const cur = map[key];
  if (typeof cur === 'string') {
    return normalizeXmlFieldPersistPath(cur);
  }
  if (cur && typeof cur === 'object' && typeof (cur as Hl7FieldMapMetaValue).path === 'string') {
    return normalizeXmlFieldPersistPath((cur as Hl7FieldMapMetaValue).path);
  }
  return undefined;
}

/**
 * UI / persisted path: turn a flat HL7 component key into slash form when it has a sub-component.
 * {@code OBX.13.1} → {@code OBX.13/OBX.13.1}; already-slash tails and non-keys are unchanged.
 */
export function expandHl7ComponentKeyToSlashNotation(key: string): string {
  const k = (key || '').trim().replace(/^\/+/, '');
  if (!k || k.includes('/') || k.includes('[') || k.includes('=')) {
    return k;
  }
  if (!/^[A-Za-z][A-Za-z0-9]{1,3}\.\d+(?:\.\d+)+$/.test(k)) {
    return k;
  }
  const parts = k.split('.');
  if (parts.length < 3) {
    return k;
  }
  const seg = (parts[0] || '').toUpperCase();
  let running = seg;
  const accum: string[] = [running];
  for (let i = 1; i < parts.length; i++) {
    running = `${running}.${parts[i]}`;
    accum.push(running);
  }
  return accum.slice(1).join('/');
}

/**
 * Inverse of {@link expandHl7ComponentKeyToSlashNotation} for a predicate LHS so parsed paths
 * populate Build Path filter rows with map option names (e.g. {@code OBX.11.1}).
 * @example collapseHl7SlashPredicateAttributeToFlatKey('OBX.2/OBX.2.2/OBX.2.2.4') // 'OBX.2.2.4'
 */
export function collapseHl7SlashPredicateAttributeToFlatKey(attr: string): string {
  const a = (attr || '').trim();
  if (!a.includes('/')) {
    return a;
  }
  const segs = a.split('/').map((s) => s.trim()).filter(Boolean);
  if (segs.length < 2) {
    return a;
  }
  const head = segs[0];
  const headSeg = (head.match(/^([A-Za-z][A-Za-z0-9]{1,3})/i)?.[1] || '').toUpperCase();
  if (!headSeg || !head.toUpperCase().startsWith(headSeg)) {
    return a;
  }
  let prev = head;
  for (let i = 1; i < segs.length; i++) {
    const cur = segs[i];
    if (!cur.toUpperCase().startsWith(`${headSeg}.`) || !cur.startsWith(`${prev}.`)) {
      return a;
    }
    prev = cur;
  }
  return segs[segs.length - 1];
}

/** Escape single quotes inside HL7 predicate literals. */
export function escapeHl7PredicateValue(v: string): string {
  return `${v}`.replace(/'/g, "\\'");
}

/** One OBR or OBX component filter: quoted string, or bare number. */
function hl7ComponentPredicatePair(componentKey: string, rawValue: string): string {
  const k = expandHl7ComponentKeyToSlashNotation((componentKey || '').trim());
  const v = (rawValue ?? '').trim();
  if (!k || v === '') {
    return '';
  }
  if (/^-?\d+(\.\d+)?$/.test(v)) {
    return `${k}=${v}`;
  }
  return `${k}='${escapeHl7PredicateValue(v)}'`;
}

/**
 * Builds persisted path for an OBX leaf under repeating OBR. Resolves parent OBR by field values (any matching OBR),
 * not by a fixed OBR occurrence inside {@code @parentId}. Example:
 * {@code HL7Message/OBX[@parentId=/HL7Message/OBR[OBR.4/OBR.4.1='UrineCytology']/@id and OBX.3/OBX.3.1='Jars'][1]/OBX.5/OBX.5.1}
 */
export function buildHl7ObxParentIdPath(args: {
  obrComponents: { name: string; value: string }[];
  obxComponents: { name: string; value: string }[];
  obxOccurrence: number;
  /** @deprecated OBR index is not embedded in {@code @parentId}; matching is by predicates only. */
  obrOccurrence?: number;
  leafTail: string;
}): string {
  const obrFilled = args.obrComponents.filter((c) => (c.value || '').trim());
  const obrPred = obrFilled
    .map((c) => hl7ComponentPredicatePair(c.name, c.value))
    .filter(Boolean)
    .join(' and ');
  const obrPathInner = obrPred ? `HL7Message/OBR[${obrPred}]` : `HL7Message/OBR`;
  const parentIdRhs = `/${obrPathInner}/@id`;
  const obxFilled = args.obxComponents.filter((c) => (c.value || '').trim());
  const obxPred = obxFilled
    .map((c) => hl7ComponentPredicatePair(c.name, c.value))
    .filter(Boolean)
    .join(' and ');
  const predInner = `[@parentId=${parentIdRhs}${obxPred ? ` and ${obxPred}` : ''}]`;
  const n = Math.max(1, Math.floor(Number(args.obxOccurrence) || 1));
  const tailRaw = (args.leafTail || '').trim().replace(/^\/+/, '');
  const tail = tailRaw ? expandHl7ComponentKeyToSlashNotation(tailRaw) : '';
  const body = `HL7Message/OBX${predInner}[${n}]/${tail}`.replace(/\/+/g, '/');
  return normalizeXmlFieldPersistPath(body);
}

/**
 * Builds persisted path for a field on a repeating OBR segment (no OBX parentId).
 * Example: {@code HL7Message/OBR[OBR.4/OBR.4.1='test'][1]/OBR.11/OBR.11.1}
 */
export function buildHl7ObrRepeatPath(args: {
  obrComponents: { name: string; value: string }[];
  obrOccurrence: number;
  leafTail: string;
}): string {
  const obrFilled = args.obrComponents.filter((c) => (c.value || '').trim());
  const obrPred = obrFilled
    .map((c) => hl7ComponentPredicatePair(c.name, c.value))
    .filter(Boolean)
    .join(' and ');
  const n = Math.max(1, Math.floor(Number(args.obrOccurrence) || 1));
  const tailRaw = (args.leafTail || '').trim().replace(/^\/+/, '');
  const tail = tailRaw ? expandHl7ComponentKeyToSlashNotation(tailRaw) : '';
  const obrSeg = obrPred ? `OBR[${obrPred}][${n}]` : `OBR[${n}]`;
  const body = `HL7Message/${obrSeg}/${tail}`.replace(/\/+/g, '/');
  return normalizeXmlFieldPersistPath(body);
}

function mergeHl7KeyedField(
  map: Record<string, Hl7FlattenedFieldMapValue>,
  baseKey: string,
  xpath: string,
  isRepeatable: boolean,
  repeatableNodes: string[] | undefined,
  leafElementName: string,
  hl7Locate?: Hl7ObrObxLocateMeta
): void {
  const base = (baseKey || '').trim();
  const xp = (xpath || '').trim();
  if (!base || !xp) {
    return;
  }
  const norm = normalizeXmlFieldPersistPath(xp);
  let key = base;
  if (map[key]) {
    const existing = existingPathInMap(map, key);
    if (existing && pathsEqualHl7(existing, norm)) {
      return;
    }
    const parent = parentSegmentBeforeElement(xp, leafElementName);
    let candidate = parent ? `${parent}.${base}` : base;
    let n = 2;
    while (map[candidate]) {
      const ex = existingPathInMap(map, candidate);
      if (ex && pathsEqualHl7(ex, norm)) {
        return;
      }
      candidate = parent ? `${parent}.${base}.${n}` : `${base}.${n}`;
      n++;
    }
    key = candidate;
  }
  const nodes = repeatableNodes?.length ? [...repeatableNodes] : undefined;
  map[key] = {
    path: norm,
    isRepeatable,
    ...(nodes?.length ? { repeatableNodes: nodes } : {}),
    ...(hl7Locate ? { hl7ObrObxLocate: hl7Locate } : {}),
  };
}

function isLeafHl7Field(n: Hl7Node): boolean {
  return !(n.children?.length) && !(n.attributes?.length);
}

export function collectObrFieldKeys(obr: Hl7Node): string[] {
  return (obr.attributes ?? []).map((a) => (a.name || '').trim()).filter(Boolean);
}

export function collectObxFieldKeys(obx: Hl7Node): string[] {
  const fromAttrs = (obx.attributes ?? []).map((a) => (a.name || '').trim()).filter(Boolean);
  const fromChildren = (obx.children ?? [])
    .filter((c) => isLeafHl7Field(c))
    .map((c) => (c.name || '').trim())
    .filter(Boolean);
  return [...new Set([...fromAttrs, ...fromChildren])];
}

function leafTailAfterObxSegment(xpath: string): string {
  const normalized = normalizeXmlFieldPersistPath(xpath).replace(/\\/g, '/');
  const marker = '/OBX/';
  const i = normalized.indexOf(marker);
  if (i < 0) {
    return '';
  }
  return normalized.slice(i + marker.length);
}

interface ObrObxCtx {
  obr: Hl7Node;
  obx: Hl7Node;
}

function walkHl7Flatten(
  map: Record<string, Hl7FlattenedFieldMapValue>,
  node: Hl7Node,
  repeatableStack: string[],
  obrObxCtx: ObrObxCtx | null
): void {
  const nextRepeat = node.isRepeatable ? [...repeatableStack, node.name.trim()] : repeatableStack;
  const hasRep = nextRepeat.length > 0;

  if (node.name === 'OBR' && node.isRepeatable) {
    const obxChild = (node.children ?? []).find((c) => c.name === 'OBX');
    const ctx = obxChild ? { obr: node, obx: obxChild } : null;
    const obrFilterKeys = collectObrFieldKeys(node);
    for (const a of node.attributes ?? []) {
      if (isLeafHl7Field(a) && (a.xpath || '').trim()) {
        const leafKey = (a.name || '').trim();
        const hl7Locate: Hl7ObrObxLocateMeta | undefined = obrFilterKeys.length
          ? {
              obrFieldKeys: obrFilterKeys,
              obxFieldKeys: [],
              leafTail: expandHl7ComponentKeyToSlashNotation(leafKey) || leafKey,
            }
          : undefined;
        mergeHl7KeyedField(
          map,
          leafKey,
          a.xpath,
          hasRep,
          hasRep ? [...nextRepeat] : undefined,
          leafKey,
          hl7Locate
        );
      }
    }
    for (const ch of node.children ?? []) {
      walkHl7Flatten(map, ch, nextRepeat, ctx);
    }
    return;
  }

  const emitLeaf = (ch: Hl7Node) => {
    if (!isLeafHl7Field(ch) || !(ch.xpath || '').trim()) {
      return;
    }
    const xp = ch.xpath.trim();
    const underObrObx = !!(obrObxCtx && xp.includes('/OBR/OBX/'));
    const hl7Locate: Hl7ObrObxLocateMeta | undefined =
      underObrObx && obrObxCtx
        ? {
            obrFieldKeys: collectObrFieldKeys(obrObxCtx.obr),
            obxFieldKeys: collectObxFieldKeys(obrObxCtx.obx),
            leafTail: (ch.name || '').trim() || leafTailAfterObxSegment(xp),
          }
        : undefined;
    mergeHl7KeyedField(
      map,
      ch.name.trim(),
      xp,
      hasRep,
      hasRep ? [...nextRepeat] : undefined,
      ch.name.trim(),
      hl7Locate
    );
  };

  for (const ch of node.children ?? []) {
    if (isLeafHl7Field(ch) && (ch.xpath || '').trim()) {
      emitLeaf(ch);
    } else {
      walkHl7Flatten(map, ch, nextRepeat, obrObxCtx);
    }
  }
  for (const a of node.attributes ?? []) {
    if (isLeafHl7Field(a) && (a.xpath || '').trim()) {
      emitLeaf(a);
    } else {
      walkHl7Flatten(map, a, nextRepeat, obrObxCtx);
    }
  }
}

export function flattenHl7FieldKeyMapDocument(doc: unknown): Record<string, Hl7FlattenedFieldMapValue> {
  if (!isHl7TreeRoot(doc)) {
    return {};
  }
  const out: Record<string, Hl7FlattenedFieldMapValue> = {};
  walkHl7Flatten(out, doc, [], null);
  return out;
}

export const flattenedHl7FieldKeyMap = flattenHl7FieldKeyMapDocument(hl7FieldKeyMapRaw as unknown);
